import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { printAccountStatement } from '../../../utils/printAccountStatement';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  Alert,
  Breadcrumbs,
  Grid,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  TableSortLabel,
  Autocomplete,
  TablePagination
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getAccountTransactions, getAccounts, confirmTransactionBalance } from '../../../services/financeService';
import { useAuth } from '../../../context/AuthContext';
import { usersApi } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Transaction, Account } from '../../../services/financeService';
import type { User } from '../../../types/user';

// تفعيل plugin UTC
dayjs.extend(utc);

// مكون كشف الحساب الرئيسي
const AccountStatements: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  // التحقق من صلاحيات الوصول للصفحة
  const canAccessPage = hasPermission('finance', 'account-statements', 'view_page');
  
  // حالة البيانات
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [userAccounts, setUserAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [accountBalance, setAccountBalance] = useState<number>(0);
  const [initialBalance, setInitialBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0 });
  
  // حالة Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // حالة الفلترة
  const [filter, setFilter] = useState({
    startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    transactionType: '',
    search: '',
    showRealOnly: true,
    sortBy: 'sequentialId',
    sortDirection: 'desc' as 'asc' | 'desc'
  });
  
  // تحميل الحسابات المتاحة للمستخدم
  useEffect(() => {
    // لا نقوم بتحميل البيانات إذا لم يكن لدى المستخدم صلاحية الوصول
    if (!canAccessPage) {
      return;
    }
    
    const fetchUserAccounts = async () => {
      try {
        setLoading(true);
        
        // استيراد apiWithContext
        const { apiWithContext } = await import('../../../services/api');
        
        // 1. جلب جميع الحسابات مع سياق كشف الحساب
        const allAccountsResult = await apiWithContext.get('/finance/accounts', 'account-statement').then(res => res.data);
        setAccounts(allAccountsResult);
        
        // 2. جلب الحسابات المخصصة للمستخدم
        // أولا: التحقق من وجود حسابات مخصصة للمستخدم في بيانات المستخدم
        const userAccessibleAccounts = (user as any)?.accessibleAccounts || [];
        
        // إذا كان لدى المستخدم حسابات مخصصة في قاعدة البيانات
        if (userAccessibleAccounts.length > 0) {
          // تصفية الحسابات المتاحة للمستخدم
          const filteredUserAccounts = allAccountsResult.filter(
            account => userAccessibleAccounts.includes(account.id)
          );
          
          setUserAccounts(filteredUserAccounts);
          
          // إذا كان هناك حسابات، حدد الحساب الأول افتراضيًا
          if (filteredUserAccounts.length > 0) {
            setSelectedAccount(filteredUserAccounts[0].id);
          }
        }
        // ثانيا: إذا لم تكن هناك حسابات مخصصة في قاعدة البيانات، استخدم تفضيلات التخزين المحلي
        else {
          const userPreferences = localStorage.getItem('userAccountPreferences');
          
          if (userPreferences) {
            const parsedPreferences = JSON.parse(userPreferences);
            const selectedAccountIds = parsedPreferences.selectedAccounts || [];
            
            // تصفية الحسابات التي اختارها المستخدم فقط
            const filteredUserAccounts = allAccountsResult.filter(
              account => selectedAccountIds.includes(account.id)
            );
            
            setUserAccounts(filteredUserAccounts);
            
            // إذا كان هناك حسابات، حدد الحساب الأول افتراضيًا
            if (filteredUserAccounts.length > 0) {
              setSelectedAccount(filteredUserAccounts[0].id);
            }
          } else {
            // إذا لم تكن هناك تفضيلات، اعرض جميع الحسابات
            setUserAccounts(allAccountsResult);
            
            // حدد الحساب الأول افتراضيًا إذا وجد
            if (allAccountsResult.length > 0) {
              setSelectedAccount(allAccountsResult[0].id);
            }
          }
        }
      } catch (err: any) {
        console.error('خطأ في تحميل الحسابات:', err);
        setError(err.message || 'حدث خطأ أثناء تحميل الحسابات');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserAccounts();
  }, [user, canAccessPage]);
  
  // تحميل البيانات عند تحميل الصفحة أو تغيير pagination
  useEffect(() => {
    if (!canAccessPage || !selectedAccount) return;
    fetchAccountTransactions(selectedAccount);
  }, [selectedAccount, page, rowsPerPage, canAccessPage]);
  
  // تحميل البيانات عند تغيير الفلتر بعد فترة زمنية للأداء الأفضل
  useEffect(() => {
    if (!selectedAccount) return;
    const timer = setTimeout(() => {
      fetchAccountTransactions(selectedAccount);
    }, 500);

    return () => clearTimeout(timer);
  }, [filter]);

  // وظيفة تغيير الفرز عند النقر على عنوان العمود
  const handleSort = (column: string) => {
    setFilter(prev => ({
      ...prev,
      sortBy: column,
      sortDirection: prev.sortBy === column && prev.sortDirection === 'desc' ? 'asc' : 'desc'
    }));
  };

  // وظيفة تأكيد الرصيد
  const handleConfirmBalance = async (transactionId: string) => {
    try {
      setLoading(true);
      const response = await confirmTransactionBalance(transactionId);
      
      if (response.success) {
        // تحديث المعاملة في القائمة المحلية
        setTransactions(prev =>
          prev.map(t =>
            t.id === transactionId
              ? { ...t, balanceConfirmed: true, balanceConfirmedAt: new Date().toISOString() }
              : t
          )
        );
      } else {
        setError(response.message || 'فشل تأكيد الرصيد');
      }
    } catch (err: any) {
      console.error('خطأ في تأكيد الرصيد:', err);
      setError(err.message || 'حدث خطأ أثناء تأكيد الرصيد');
    } finally {
      setLoading(false);
    }
  };

  // وظيفة تحميل المعاملات
  const fetchAccountTransactions = async (accountId: string) => {
    if (!accountId) {
      setError("معرّف الحساب غير موجود");
      return;
    }
    
    console.log(`بدء تحميل معاملات الحساب: ${accountId}، صفحة ${page + 1}، مع الفلاتر:`, filter);
    setLoading(true);
    setError(null);
    
    try {
      // استخدام apiWithContext لإرسال السياق مع الطلب
      const { apiWithContext } = await import('../../../services/api');
      const result = await apiWithContext.get(`/finance/transactions/account/${accountId}`, 'account-statement', {
        params: {
          ...filter,
          page: page + 1,
          limit: rowsPerPage
        }
      }).then(res => res.data);
      
      console.log("استجابة API معاملات الحساب:", result);
      
      // التحقق من وجود خطأ في الاستجابة
      if (result.error) {
        setError(result.error);
        console.error('خطأ في تحميل معاملات الحساب:', result.error);
      }
      
      setTransactions(result.data || result.transactions || []);
      setTotalItems(result.total || result.totalCount || 0);
      setTotals(result.totals || { totalDebit: 0, totalCredit: 0 });
      
      // تعيين معلومات الحساب
      if (result.account) {
        setAccountName(result.account.name);
        setAccountNumber(result.account.accountNumber);
        setAccountBalance(result.account.currentBalance || 0);
        setInitialBalance(result.account.initialBalance || 0);
      }
    } catch (err: any) {
      console.error('خطأ في تحميل معاملات الحساب:', err);
      setError(err.message || 'حدث خطأ غير متوقع أثناء تحميل معاملات الحساب');
    } finally {
      setLoading(false);
    }
  };
  
  // وظيفة تصدير البيانات إلى ملف Excel
  const exportToExcel = async () => {
    if (!selectedAccount) return;
    
    try {
      // عرض حالة التحميل
      setLoading(true);
      
      // طلب جميع المعاملات المفلترة (بدون تقسيم صفحات)
      const allData = await getAccountTransactions(selectedAccount, {
        ...filter,
        page: 1,
        limit: totalItems || 999999 // استخدام العدد الكلي للمعاملات أو رقم كبير جداً
      });
      
      const allTransactions = allData.data || allData.transactions || [];
      
      // تحضير البيانات للتصدير
      const dataToExport = allTransactions.map((transaction: any) => ({
        'رقم المعاملة': transaction.sequentialId,
        'التاريخ': dayjs.utc(transaction.date).format('DD/MM/YYYY'),
        'الوصف': transaction.description,
        'نوع المعاملة': transaction.referenceType,
        'المرجع': transaction.referenceNumber,
        'مدين': transaction.debit,
        'دائن': transaction.credit,
        'الرصيد': transaction.balance,
        'الملاحظات': transaction.notes || '',
        'تأكيد الرصيد': transaction.balanceConfirmed ? 'تم التأكيد' : 'غير مؤكد',
        'تاريخ التأكيد': transaction.balanceConfirmedAt ? dayjs(transaction.balanceConfirmedAt).format('DD/MM/YYYY HH:mm') : '-'
      }));
      
      // إنشاء ورقة عمل جديدة
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // تعديل عرض الأعمدة
      const columnWidths = [
        { wch: 12 }, // رقم المعاملة
        { wch: 12 }, // التاريخ
        { wch: 30 }, // الوصف
        { wch: 15 }, // نوع المعاملة
        { wch: 12 }, // المرجع
        { wch: 12 }, // مدين
        { wch: 12 }, // دائن
        { wch: 12 }, // الرصيد
        { wch: 25 }, // الملاحظات
        { wch: 15 }, // تأكيد الرصيد
        { wch: 18 }  // تاريخ التأكيد
      ];
      worksheet['!cols'] = columnWidths;
      
      // إنشاء كتاب عمل جديد وإضافة ورقة العمل إليه
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'كشف الحساب');
      
      // إنشاء اسم الملف
      const fileName = `كشف_حساب_${accountNumber}_${accountName}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      
      // تصدير الملف
      XLSX.writeFile(workbook, fileName);
      
      console.log(`تم تصدير ${dataToExport.length} معاملة بنجاح (من أصل ${totalItems}) إلى: ${fileName}`);
      setLoading(false);
    } catch (error) {
      console.error('حدث خطأ أثناء تصدير البيانات:', error);
      setError('حدث خطأ أثناء تصدير البيانات إلى Excel');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // وظيفة تغيير الفلتر
  const handleFilterChange = (name: string, value: string | boolean) => {
    // إذا كان التغيير في نوع المعاملة، نطبع قيمة المدخل للتشخيص
    if (name === 'transactionType') {
      console.log(`تم تحديد نوع المعاملة: "${value}"`);
    }
    
    setFilter(prev => ({ ...prev, [name]: value }));
    setPage(0); // إعادة تعيين الصفحة عند تغيير الفلتر
  };
  
  // وظيفة تغيير الصفحة
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // وظيفة تغيير عدد الصفوف
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // إذا لم يكن للمستخدم صلاحية الوصول للصفحة، عرض رسالة خطأ
  if (!canAccessPage) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.error.light, 0.1), border: `1px solid ${theme.palette.error.light}` }}>
          <Typography variant="h6" color="error">
            <span role="img" aria-label="تحذير">⚠️</span> لا تملك صلاحية الوصول إلى هذه الصفحة
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
            لم يتم منحك صلاحية الوصول إلى صفحة كشف الحساب. يرجى التواصل مع مدير النظام للحصول على الصلاحيات اللازمة.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {/* فتات الخبز ورأس الصفحة */}
        <Box sx={{ mb: 3 }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
              <Link to="/finance/accounts" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                الحسابات
              </Link>
              <Typography color="text.primary">كشف حساب</Typography>
            </Breadcrumbs>
            
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <AccountBalanceIcon sx={{ mr: 1, color: '#30c3b0' }} />
              كشف حساب
            </Typography>
            <Typography variant="body1" color="text.secondary">
              عرض كشوفات الحسابات والمعاملات المالية
            </Typography>
          </Box>
        </Box>

        {/* اختيار الحساب */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <Autocomplete
              id="account-select"
              options={userAccounts}
              getOptionLabel={(option) => `${option.accountNumber} - ${option.name}`}
              value={userAccounts.find(account => account.id === selectedAccount) || null}
              onChange={(_, newValue) => {
                setSelectedAccount(newValue?.id || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر الحساب"
                  variant="outlined"
                  placeholder="البحث في الحسابات..."
                />
              )}
            />
          </FormControl>
        </Box>

        {/* عرض معلومات الحساب المختار */}
        {selectedAccount && !loading && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: alpha(theme.palette.primary.main, 0.05), 
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">اسم الحساب</Typography>
                <Typography variant="body1" fontWeight={500}>{accountName}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">رقم الحساب</Typography>
                <Typography variant="body1" fontWeight={500}>{accountNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">الرصيد الحالي</Typography>
                <Typography variant="body1" fontWeight={600} color="primary.main">
                  {accountBalance.toLocaleString()} ريال
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {/* فلاتر البحث */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch
                checked={filter.showRealOnly}
                onChange={(e) => handleFilterChange('showRealOnly', e.target.checked)}
                color="primary"
              />
            }
            label="عرض المعاملات الحقيقية فقط"
            labelPlacement="start"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                color: 'text.secondary',
                mr: 1
              }
            }}
          />
        </Box>
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="من تاريخ"
            type="date"
            size="small"
            value={filter.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 auto', minWidth: '140px' }}
          />
          
          <TextField
            label="إلى تاريخ"
            type="date"
            size="small"
            value={filter.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 auto', minWidth: '140px' }}
          />
          
          <FormControl size="small" sx={{ flex: '1 1 auto', minWidth: '180px' }}>
            <InputLabel id="transaction-type-label">نوع المعاملة</InputLabel>
            <Select
              labelId="transaction-type-label"
              id="transaction-type-select"
              value={filter.transactionType}
              label="نوع المعاملة"
              onChange={(e) => handleFilterChange('transactionType', e.target.value)}
            >
              <MenuItem value="">الكل</MenuItem>
              <MenuItem value="قيد يومية">قيد يومية</MenuItem>
              <MenuItem value="فاتورة">فاتورة</MenuItem>
              <MenuItem value="سند قبض">سند قبض</MenuItem>
              <MenuItem value="سند صرف">سند صرف</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ flex: '1 1 auto', minWidth: '150px' }}>
            <InputLabel id="sort-by-label">ترتيب حسب</InputLabel>
            <Select
              labelId="sort-by-label"
              id="sort-by-select"
              value={filter.sortBy}
              label="ترتيب حسب"
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <MenuItem value="sequentialId">رقم المعاملة</MenuItem>
              <MenuItem value="date">التاريخ</MenuItem>
              <MenuItem value="referenceType">نوع المعاملة</MenuItem>
              <MenuItem value="debit">المدين</MenuItem>
              <MenuItem value="credit">الدائن</MenuItem>
              <MenuItem value="balance">الرصيد</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ flex: '0 1 auto', minWidth: '100px' }}>
            <InputLabel id="sort-direction-label">الترتيب</InputLabel>
            <Select
              labelId="sort-direction-label"
              id="sort-direction-select"
              value={filter.sortDirection}
              label="الترتيب"
              onChange={(e) => handleFilterChange('sortDirection', e.target.value as 'asc' | 'desc')}
            >
              <MenuItem value="desc">تنازلي</MenuItem>
              <MenuItem value="asc">تصاعدي</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="بحث"
            size="small"
            value={filter.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="بحث في الوصف أو المرجع..."
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ flex: '2 1 auto', minWidth: '220px' }}
          />
          
          <Tooltip title="تحديث" arrow>
            <IconButton
              onClick={() => selectedAccount && fetchAccountTransactions(selectedAccount)}
              color="primary"
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* عرض حالة التحميل */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <>
            {/* ملخص الأرصدة */}
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              mb: 3,
              p: 2,
              bgcolor: alpha(theme.palette.primary.light, 0.05),
              borderRadius: 1
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', p: 1 }}>
                <Typography variant="caption" color="text.secondary">الرصيد الابتدائي</Typography>
                <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                  {initialBalance.toLocaleString()} ريال
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', p: 1 }}>
                <Typography variant="caption" color="text.secondary">إجمالي المدين</Typography>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                  {totals.totalDebit.toLocaleString()} ريال
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', p: 1 }}>
                <Typography variant="caption" color="text.secondary">إجمالي الدائن</Typography>
                <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                  {totals.totalCredit.toLocaleString()} ريال
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 auto', p: 1 }}>
                <Typography variant="caption" color="text.secondary">الرصيد الحالي</Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                  {accountBalance.toLocaleString()} ريال
                </Typography>
              </Box>
            </Box>
            
            {/* جدول المعاملات */}
            <TableContainer sx={{ maxHeight: 600, overflowY: 'auto', border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, borderRadius: 1 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{
                    '& th': {
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main
                    }
                  }}>
                    <TableCell>
                      <TableSortLabel
                        active={filter.sortBy === 'sequentialId'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('sequentialId')}
                      >
                        رقم المعاملة
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filter.sortBy === 'date'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('date')}
                      >
                        التاريخ
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filter.sortBy === 'description'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('description')}
                      >
                        الوصف
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filter.sortBy === 'referenceType'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('referenceType')}
                      >
                        نوع المعاملة
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filter.sortBy === 'referenceNumber'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('referenceNumber')}
                      >
                        المرجع
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={filter.sortBy === 'debit'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('debit')}
                      >
                        مدين
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={filter.sortBy === 'credit'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('credit')}
                      >
                        دائن
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={filter.sortBy === 'balance'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('balance')}
                      >
                        الرصيد
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filter.sortBy === 'notes'}
                        direction={filter.sortDirection}
                        onClick={() => handleSort('notes')}
                      >
                        الملاحظات
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">تأكيد الرصيد</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction, index) => (
                      <TableRow
                        key={transaction.id}
                        hover
                        sx={{
                          bgcolor: index % 2 === 0 ? 'background.paper' : alpha(theme.palette.primary.light, 0.03),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.light, 0.08),
                          }
                        }}
                      >
                        <TableCell>
                          {/* عمود رقم المعاملة التسلسلي */}
                          <Typography variant="body2" fontWeight={500}>
                            {transaction.sequentialId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {dayjs.utc(transaction.date).format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={transaction.referenceType}
                            color={
                              transaction.referenceType === 'قيد يومية' ? 'primary' :
                              transaction.referenceType === 'سند قبض' ? 'success' :
                              transaction.referenceType === 'سند صرف' ? 'error' :
                              'info'
                            }
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          {transaction.referenceNumber ? (
                            (() => {
                              let linkPath = '#';
                              const refType = String(transaction.referenceType || '');
                              const refId = (transaction as any).referenceId;
                              
                              if (refType === 'سند قبض') {
                                linkPath = `/finance/receipts/view/${transaction.referenceNumber}`;
                              } else if (refType === 'سند صرف') {
                                linkPath = `/finance/payments/view/${transaction.referenceNumber}`;
                              } else if (refType === 'قيد يومية') {
                                linkPath = `/finance/journal-entries/view/${transaction.referenceNumber}`;
                              } else if (refType.includes('فاتورة') && refId) {
                                // للفواتير نحتاج pageId (referenceId) و invoiceId (referenceNumber)
                                linkPath = `/invoices/${refId}/${transaction.referenceNumber}/view`;
                              }
                              
                              return (
                                <Link
                                  to={linkPath}
                                  style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                                >
                                  {transaction.referenceNumber}
                                </Link>
                              );
                            })()
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{
                          color: transaction.debit > 0 ? 'success.main' : 'text.secondary',
                          fontWeight: transaction.debit > 0 ? 500 : 400
                        }}>
                          {transaction.debit > 0 ? transaction.debit.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{
                          color: transaction.credit > 0 ? 'error.main' : 'text.secondary',
                          fontWeight: transaction.credit > 0 ? 500 : 400
                        }}>
                          {transaction.credit > 0 ? transaction.credit.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main
                        }}>
                          {transaction.balance.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{
                          maxWidth: 200,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {transaction.notes || '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              transaction.balanceConfirmed && transaction.balanceConfirmedAt
                                ? `تم التأكيد في: ${dayjs(transaction.balanceConfirmedAt).format('DD/MM/YYYY HH:mm')}`
                                : 'اضغط لتأكيد الرصيد'
                            }
                            arrow
                          >
                            <span>
                              <Button
                                size="small"
                                variant={transaction.balanceConfirmed ? "contained" : "outlined"}
                                color="success"
                                disabled={transaction.balanceConfirmed || loading}
                                onClick={() => handleConfirmBalance(transaction.id)}
                                startIcon={transaction.balanceConfirmed ? <CheckCircleIcon /> : null}
                                sx={{
                                  minWidth: '100px',
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  px: 1.5,
                                }}
                              >
                                {transaction.balanceConfirmed ? 'تم التأكيد' : 'تأكيد'}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          لا توجد معاملات متاحة للفترة المحددة
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage="عدد الصفوف في الصفحة:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
              }
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
            
            {/* أزرار الإجراءات */}
            <Box sx={{ mt: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                تم عرض {transactions.length} معاملة من إجمالي {totalItems}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    if (!selectedAccount) return;
                    try {
                      setLoading(true);
                      // طلب جميع المعاملات للطباعة
                      const result = await getAccountTransactions(selectedAccount, {
                        ...filter,
                        page: 1,
                        limit: totalItems // استخدام العدد الإجمالي
                      });
                      
                      printAccountStatement({
                        accountName,
                        accountNumber,
                        accountBalance,
                        initialBalance,
                        startDate: filter.startDate,
                        endDate: filter.endDate,
                        transactions: result.data || result.transactions || [],
                        totals: result.totals || totals
                      });
                      setLoading(false);
                    } catch (err) {
                      console.error('خطأ:', err);
                      setLoading(false);
                      alert('حدث خطأ أثناء تحميل البيانات');
                    }
                  }}
                  startIcon={<i className="fas fa-print" />}
                  disabled={loading}
                >
                  طباعة كشف الحساب
                </Button>
                
                <Button
                  variant="outlined"
                  size="small"
                  color="success"
                  onClick={exportToExcel}
                  startIcon={<FileDownloadIcon />}
                  disabled={transactions.length === 0 || loading}
                >
                  تصدير إلى Excel
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AccountStatements;